import * as THREE from 'three';
import { ENV, ENEMY_MISSILE } from '../core/SimConfig.js';

export const EnemyMissileState = Object.freeze({
  READY: 0,
  FIRED: 1,
  DEAD: 2,
});

// ── Reusable temp scratch (zero-alloc hot path) ──────────────────────────────
const _r = new THREE.Vector3();
const _vRel = new THREE.Vector3();
const _aimPoint = new THREE.Vector3();
const _aimDir = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _lateral = new THREE.Vector3();
const _accel = new THREE.Vector3();
const _vNorm = new THREE.Vector3();
const _spawnRot = new THREE.Quaternion();
const _spawnEuler = new THREE.Euler();
const NEG_Z = new THREE.Vector3(0, 0, -1);

const DRAG_K = 0.5 * ENV.airDensity * ENEMY_MISSILE.dragCd * ENEMY_MISSILE.bodyArea / ENEMY_MISSILE.mass;
const THRUST_ACCEL = ENEMY_MISSILE.thrust / ENEMY_MISSILE.mass;

// Resolve target pos/vel — works for both `entity.flight.position/velocity`
// (AI entities) and `entity.position/velocity` (the player Aircraft).
function targetPos(t) { return t.flight ? t.flight.position : t.position; }
function targetVel(t) { return t.flight ? t.flight.velocity : t.velocity; }

/**
 * Generic enemy heat-seeking missile. Same physics + PN guidance as the
 * AIM-9 but with no flame mesh and no pylon mounting — meshes are spawned
 * in scene root at fire time at the launcher's origin.
 *
 * Target API: accepts any entity that exposes `position` + `velocity`
 * directly (player Aircraft) or via `.flight` (enemy entities), and
 * optionally `hitVolume` + `onProjectileHit`.
 */
export default class EnemyMissile {
  /**
   * @param {{ modelMesh: THREE.Object3D, scene: THREE.Scene }} opts
   */
  constructor({ modelMesh, scene }) {
    this.scene = scene;
    this.mesh = modelMesh;
    this.mesh.visible = false;

    this.state = EnemyMissileState.READY;

    this.position = new THREE.Vector3();
    this.velocity = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();
    this.launchPosition = new THREE.Vector3();
    this.age = 0;

    this.target = null;
    this.detonation = null;
    this.smokeTrailHandle = -1;

    this.prevX = 0;
    this.prevY = 0;
    this.prevZ = 0;
  }

  /**
   * Begin free flight from the given world pose, locked onto `target`.
   * The mesh is added to the scene root.
   *
   * @param {THREE.Vector3} position
   * @param {THREE.Quaternion} quaternion — launcher's world rotation
   * @param {THREE.Vector3} velocity     — launcher's world velocity (inherited)
   * @param {object} target              — entity with position/velocity
   */
  fire(position, quaternion, velocity, target) {
    if (this.state !== EnemyMissileState.READY) return;

    this.position.copy(position);
    this.launchPosition.copy(position);
    this.velocity.copy(velocity);

    // Compose the launcher orientation with the GLB-specific spawn rotation
    // so the model's authored forward axis aligns with the velocity direction.
    const [ex, ey, ez] = ENEMY_MISSILE.spawnRotationEuler;
    if (ex || ey || ez) {
      _spawnEuler.set(ex, ey, ez);
      _spawnRot.setFromEuler(_spawnEuler);
      this.quaternion.copy(quaternion).multiply(_spawnRot);
    } else {
      this.quaternion.copy(quaternion);
    }

    this.age = 0;
    this.target = target;
    this.state = EnemyMissileState.FIRED;

    this.mesh.position.copy(this.position);
    this.mesh.quaternion.copy(this.quaternion);
    this.mesh.visible = true;
    this.scene.add(this.mesh);
  }

  /**
   * Integrate one step of free-flight physics + PN guidance. Mirrors the
   * AIM-9 update, less the flame animation. The proximity fuse is run inside
   * here; swept hit-tests against external bodies (player hit-volume, terrain)
   * are run by the system.
   */
  update(dt) {
    if (this.state !== EnemyMissileState.FIRED) return;

    this.age += dt;
    if (this.age > ENEMY_MISSILE.maxLifetime) {
      this.detonation = { isAircraftHit: false };
      this.kill();
      return;
    }

    let steerX = 0, steerY = 0, steerZ = 0;
    const target = this.target;
    // Flares spoof the seeker: while the target is actively dispensing, the
    // missile chases a point trailing behind the aircraft instead of the
    // aircraft itself (faking a flare lock) so it normally slides off the
    // tail. The warhead stays live, though — see the proximity fuse below.
    const decoyed = target?.countermeasures?.firing === true;
    const targetAlive = target && !target.dead && !target.destroyed;

    const dx = this.position.x - this.launchPosition.x;
    const dy = this.position.y - this.launchPosition.y;
    const dz = this.position.z - this.launchPosition.z;
    const separated = (dx * dx + dy * dy + dz * dz)
      > (ENEMY_MISSILE.steerArmDistance * ENEMY_MISSILE.steerArmDistance);

    if (targetAlive && separated) {
      const tPos = targetPos(target);
      const tVel = targetVel(target);

      _r.copy(tPos).sub(this.position);
      _vRel.copy(tVel).sub(this.velocity);

      const range = _r.length();
      if (range > 0.001) {
        const closing = -(_r.x * _vRel.x + _r.y * _vRel.y + _r.z * _vRel.z) / range;

        if (decoyed) {
          // Aim at a point trailing behind the aircraft along its flight path.
          const tSpeed = tVel.length();
          _aimPoint.copy(tPos);
          if (tSpeed > 1) {
            _aimPoint.addScaledVector(tVel, -ENEMY_MISSILE.flareDecoyDistance / tSpeed);
          }
        } else {
          const tLead = range / Math.max(closing, 50);
          _aimPoint.copy(tVel).multiplyScalar(tLead).add(tPos);
        }
        _aimDir.copy(_aimPoint).sub(this.position).normalize();

        const speed = this.velocity.length();
        if (speed > 1) {
          _fwd.copy(this.velocity).divideScalar(speed);
          const along = _aimDir.dot(_fwd);
          _lateral.copy(_aimDir).addScaledVector(_fwd, -along);
          const latMag = _lateral.length();
          if (latMag > 1e-4) {
            _lateral.divideScalar(latMag);
            const angle = Math.atan2(latMag, along);
            let aMag = ENEMY_MISSILE.navConstant * Math.max(closing, speed * 0.5) * angle;
            if (aMag > ENEMY_MISSILE.maxLateralAccel) aMag = ENEMY_MISSILE.maxLateralAccel;
            steerX = _lateral.x * aMag;
            steerY = _lateral.y * aMag;
            steerZ = _lateral.z * aMag;
          }
        }

        // ── Proximity fuse: analytic closest-point-of-approach ─────────
        // Same in-frame CPA solve as the AIM-9 — prevents tunneling at
        // high closing speeds vs small player hitbox. Always armed, even
        // while decoyed: the missile aims off the tail and normally misses,
        // but a too-late flare drop or a player flying into the warhead still
        // detonates if it passes within the fuse radius.
        const fuseCenter = target.hitVolume?.center ?? tPos;
        const r0x = fuseCenter.x - tVel.x * dt - this.position.x;
        const r0y = fuseCenter.y - tVel.y * dt - this.position.y;
        const r0z = fuseCenter.z - tVel.z * dt - this.position.z;
        const vRx = tVel.x - this.velocity.x;
        const vRy = tVel.y - this.velocity.y;
        const vRz = tVel.z - this.velocity.z;
        const vR2 = vRx * vRx + vRy * vRy + vRz * vRz;
        let tStar = 0;
        if (vR2 > 1e-4) {
          tStar = -(r0x * vRx + r0y * vRy + r0z * vRz) / vR2;
          if (tStar < 0) tStar = 0;
          else if (tStar > dt) tStar = dt;
        }
        const cpaX = r0x + vRx * tStar;
        const cpaY = r0y + vRy * tStar;
        const cpaZ = r0z + vRz * tStar;
        const fuse = ENEMY_MISSILE.proximityFuse;
        if (cpaX * cpaX + cpaY * cpaY + cpaZ * cpaZ < fuse * fuse) {
          const detX = this.position.x + this.velocity.x * tStar;
          const detY = this.position.y + this.velocity.y * tStar;
          const detZ = this.position.z + this.velocity.z * tStar;
          target.onProjectileHit?.(detX, detY, detZ, ENEMY_MISSILE.damage, 'enemyMissile');
          this.position.set(detX, detY, detZ);
          this.detonation = { isAircraftHit: true };
          this.kill();
          return;
        }
      }
    }

    // ── Body forward (along velocity) ─────────────────────────────────
    const speed = this.velocity.length();
    if (speed > 0.01) {
      _vNorm.copy(this.velocity).divideScalar(speed);
    } else {
      _vNorm.set(0, 0, -1).applyQuaternion(this.quaternion);
    }

    const burning = this.age < ENEMY_MISSILE.burnTime;
    _accel.set(0, 0, 0);
    if (burning) _accel.addScaledVector(_vNorm, THRUST_ACCEL);

    if (speed > 0.01) {
      const dragMag = DRAG_K * speed * speed;
      _accel.addScaledVector(_vNorm, -dragMag);
    }

    _accel.y -= ENV.gravity;
    _accel.x += steerX;
    _accel.y += steerY;
    _accel.z += steerZ;

    this.velocity.addScaledVector(_accel, dt);

    this.prevX = this.position.x;
    this.prevY = this.position.y;
    this.prevZ = this.position.z;

    this.position.addScaledVector(this.velocity, dt);

    if (this.velocity.lengthSq() > 1) {
      _vNorm.copy(this.velocity).normalize();
      this.quaternion.setFromUnitVectors(NEG_Z, _vNorm);
    }
    this.mesh.position.copy(this.position);
    this.mesh.quaternion.copy(this.quaternion);
  }

  kill() {
    if (this.state === EnemyMissileState.DEAD) return;
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    this.mesh.visible = false;
    this.state = EnemyMissileState.DEAD;
    this.target = null;
  }

  dispose() {
    this.kill();
  }
}
